import { Component, OnDestroy, OnInit } from '@angular/core';
import { GoogleChartInterface, GoogleChartType } from 'ng2-google-charts';
import { WebsiteAuditService } from '../website-audit.service';
import { Subscription } from 'rxjs';
import { NotificationService } from 'src/app/core/services/notification.service';
import { DarkmodeService } from 'src/app/services/darkmode.service';
import { ActivatedRoute, Router } from '@angular/router';

const DONUT_BASE = [
  '#6161ff',
  '__green__',
  '#e0a544',
  '#e26a5d',
  '#5b9df0',
  '#c98ae0',
  '#4da18c',
  '#c8e6a0',
  '#dd7bb0',
];

const ACCENT = '#6161ff';
const COMP_COLOR = '#e0a544';

// Distinct line colors for each tracked competitor in the trend chart (matches
// the Overview palette). COMP_COLOR (amber) stays first.
const COMP_PALETTE = [
  '#e0a544',
  '#5b9df0',
  '#c98ae0',
  '#5fcf95',
  '#e26a5d',
  '#4da18c',
  '#dd7bb0',
  '#c8e6a0',
];

@Component({
  selector: 'app-diagnostic',
  templateUrl: './diagnostic.component.html',
  styleUrls: ['../audit-theme.css', './diagnostic.component.css'],
})
export class DiagnosticComponent implements OnInit, OnDestroy {
  constructor(
    private websiteAuditService: WebsiteAuditService,
    private notificationService: NotificationService,
    private darkmodeService: DarkmodeService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  private subs: Subscription = new Subscription();
  // Overlay shown while the sub-bar swaps competitors.
  public competitorSwitching$ = this.websiteAuditService.getCompetitorSwitching();
  private selectedTask: any = null;
  private isDark = true;

  private donutPalette(): string[] {
    const green = this.isDark ? '#5fcf95' : '#16a34a';
    return DONUT_BASE.map((c) => (c === '__green__' ? green : c));
  }

  public firstLoad = false;
  public isLoading = false;

  public taskErrorsData: any = null;

  // page bars
  public pageBars: any[] = [];
  // donut
  public donutSegments: any[] = [];
  public donutTotal = 0;
  public donutR = 64;

  // total errors over time card
  public errorSummaryData: {
    currentErrors: number;
    deltaText: string;
    deltaColor: string;
    hasDelta: boolean;
    compNote: string;
    compNoteColor: string;
  } | null = null;

  // total-errors trend (project-standard Google LineChart — same as UX/CR)
  public errorTrendHasData = false;
  // Custom clickable dash legend (Google's built-in legend is hidden). Clicking
  // the competitor entry toggles its line; "Your site" stays locked on.
  public errorTrendLegend: {
    key: string;
    label: string;
    color: string;
    isYou: boolean;
    visible: boolean;
  }[] = [];
  private trendVisible: { [key: string]: boolean } = {};
  // Last inputs, so a legend toggle can re-plot the trend.
  private lastErrorCt: any = null;
  private lastErrorComps: any[] = [];
  public errorTrendChart: GoogleChartInterface = {
    chartType: GoogleChartType.LineChart,
    dataTable: [],
    options: {
      legend: {
        position: 'top',
        alignment: 'center',
        textStyle: { fontSize: 12, fontName: 'Roboto, sans-serif', color: '#666' },
      },
      pointSize: 4,
      interpolateNulls: true,
      crosshair: { trigger: 'focus', orientation: 'vertical', color: '#eeeeee' },
      focusTarget: 'category',
      tooltip: { textStyle: { fontSize: 12 }, isHtml: true, trigger: 'focus' },
      colors: [ACCENT, COMP_COLOR],
      height: 160,
      backgroundColor: 'transparent',
      chartArea: { left: 34, right: 12, top: 24, bottom: 26, width: '100%', height: '62%' },
      vAxis: {
        minValue: 0,
        baseline: 0,
        baselineColor: '#c0c0c0',
        viewWindow: { min: 0 },
        gridlineColor: '#ededed',
        textStyle: { fontSize: 11, fontName: 'Roboto, sans-serif', color: '#666' },
      },
      hAxis: {
        textStyle: { fontSize: 11, fontName: 'Roboto, sans-serif', color: '#666' },
        gridlines: { color: 'transparent' },
        minorGridlines: { count: 0 },
        baselineColor: 'transparent',
      },
    },
  };

  // table + filters
  public errors: any[] = [];
  public types: string[] = [];
  public selectedType = 'overall';
  public pageFilterActive = 'overall';
  public sortKey = 'impact';
  public sortOrder = 'desc';

  public token = '';
  public isSharableComponent = false;

  private get deltaGreen(): string {
    return this.isDark ? '#5fcf95' : '#16a34a';
  }
  private get deltaRed(): string {
    return this.isDark ? '#e26a5d' : '#d1483b';
  }

  ngOnInit(): void {
    if (this.router.url.includes('/share/website-audit')) {
      this.token = this.route.snapshot.paramMap.get('token') as string;
      this.isSharableComponent = true;
    }

    this.subs.add(
      this.darkmodeService.getDarkMode().subscribe((isDark: boolean) => {
        // Follows the service value (the share shell sets it from the shared-with
        // theme), so the shared page matches the sharer's theme.
        this.isDark = !!isDark;
        if (this.taskErrorsData) {
          this.buildDonut();
          this.buildErrorSummary();
        }
      }),
    );

    this.subs.add(
      this.websiteAuditService.getSelectedAuditTask().subscribe((res: any) => {
        this.selectedTask = res;
        this.getAuditTaskErrors();
      }),
    );

    this.subs.add(
      this.websiteAuditService.getAuditTasksLoading().subscribe((res: any) => {
        this.isLoading = res;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ─── Data ────────────────────────────────────────────────────────────────

  private getAuditTaskErrors(): void {
    if (!this.selectedTask && !this.isSharableComponent) {
      return;
    }

    const params: any = {
      ...(this.isSharableComponent
        ? { token: this.token }
        : { taskId: this.selectedTask.taskId }),
    };

    if (this.selectedType != 'overall') params['type'] = this.selectedType;
    if (this.pageFilterActive != 'overall') params['category'] = this.pageFilterActive;
    if (this.sortKey !== '') params[this.sortKey] = this.sortOrder;

    this.websiteAuditService.updateAuditTasksLoading(true);
    this.websiteAuditService.getAuditTaskErrors(params).subscribe({
      next: (res: any) => {
        this.firstLoad = true;
        this.websiteAuditService.updateAuditTasksLoading(false);
        this.taskErrorsData = res || null;
        this.buildPageBars();
        this.buildDonut();
        this.buildTypes();
        this.buildErrorSummary();
        this.errors = this.taskErrorsData?.errors || [];
      },
      error: (err: any) => {
        this.firstLoad = true;
        this.websiteAuditService.updateAuditTasksLoading(false);
        this.taskErrorsData = null;
        this.notificationService.error(err?.error?.message);
      },
    });
  }

  private buildPageBars(): void {
    const countByPage = this.taskErrorsData?.errorStats?.countByPage || {};
    const entries = Object.keys(countByPage).map((name) => ({
      name,
      value: Number(countByPage[name]) || 0,
    }));
    entries.sort((a, b) => b.value - a.value);
    const max = entries.reduce((m, e) => Math.max(m, e.value), 0) || 1;
    this.pageBars = entries.map((e) => ({
      ...e,
      width: `${Math.round((e.value / max) * 100)}%`,
    }));
  }

  private buildDonut(): void {
    const countByType = this.taskErrorsData?.errorStats?.countByType || {};
    const entries = Object.keys(countByType)
      .map((name) => ({ name, value: Number(countByType[name]) || 0 }))
      .filter((e) => e.value > 0)
      .sort((a, b) => b.value - a.value);

    const total = entries.reduce((s, e) => s + e.value, 0);
    this.donutTotal = total;

    const palette = this.donutPalette();
    const C = 2 * Math.PI * this.donutR;
    let cum = 0;
    this.donutSegments = entries.map((e, i) => {
      const frac = total ? e.value / total : 0;
      const arc = frac * C;
      const seg = {
        name: e.name,
        value: e.value,
        color: palette[i % palette.length],
        dash: `${arc.toFixed(2)} ${(C - arc).toFixed(2)}`,
        offset: `${(-cum).toFixed(2)}`,
      };
      cum += arc;
      return seg;
    });
  }

  private buildTypes(): void {
    this.types = Object.keys(this.taskErrorsData?.errorStats?.countByType || {});
  }

  // ─── Filters / sort ──────────────────────────────────────────────────────

  sortData(sortKey: string): void {
    if (this.sortKey === sortKey) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // Switching to a new column: the list already loads descending (worst
      // first), so a fresh 'desc' would look unchanged and feel broken. Start
      // ascending so the rows (and the sort icon) visibly react on first click.
      this.sortKey = sortKey;
      this.sortOrder = 'asc';
    }
    this.getAuditTaskErrors();
  }

  typeChanged(event: any): void {
    this.selectedType = event?.value || event;
    if (this.selectedType != '') this.getAuditTaskErrors();
  }

  pageChangedFilter(page: string): void {
    this.pageFilterActive = page;
    this.getAuditTaskErrors();
  }

  clearFilters(): void {
    this.pageFilterActive = 'overall';
    this.selectedType = 'overall';
    this.getAuditTaskErrors();
  }

  // ─── Total errors over time ──────────────────────────────────────────────

  private buildErrorSummary(): void {
    const stats = this.taskErrorsData?.totalErrorsOverTimeStats;
    const ct = stats?.currentTarget;
    if (!ct) {
      this.errorSummaryData = null;
      this.errorTrendHasData = false;
      this.errorTrendChart.dataTable = [];
      return;
    }

    const current: number = ct.currentPeriod?.totalErrors ?? 0;
    const previous: number = ct.previousPeriod?.totalErrors ?? 0;
    const delta: number = ct.deltaVsPrevious ?? (current - previous);
    const deltaAbs = Math.abs(delta);
    const deltaArrow = delta > 0 ? '▲' : delta < 0 ? '▼' : '→';
    // More errors is bad (red), fewer is good (green).
    const deltaColor = delta > 0 ? this.deltaRed : delta < 0 ? this.deltaGreen : '';

    // Only the competitor picked in the sub-bar is compared/plotted.
    const selComp = this.resolveSelectedCompetitor(stats);
    let compNote = '';
    let compNoteColor = '';
    if (selComp) {
      const compName = this.getDomain(selComp.target || '');
      const compErrors: number = selComp.currentPeriod?.totalErrors ?? 0;
      if (current > compErrors) {
        compNote = `${compName} reports ${compErrors} — you carry more errors.`;
        compNoteColor = this.deltaRed;
      } else if (current < compErrors) {
        compNote = `${compName} reports ${compErrors} — fewer errors than them.`;
        compNoteColor = this.deltaGreen;
      } else {
        compNote = `Level with ${compName} at ${compErrors} errors.`;
        compNoteColor = '';
      }
    }

    this.errorSummaryData = {
      currentErrors: current,
      deltaText: `${deltaArrow} ${deltaAbs} vs last`,
      deltaColor,
      hasDelta: delta !== 0,
      compNote,
      compNoteColor,
    };

    this.buildErrorTrend(
      ct,
      Array.isArray(stats?.competitors) ? stats.competitors : [],
    );
  }

  // Prefer the sub-bar's selected competitor; if none is selected, fall back to
  // the first one so the chart still shows a benchmark.
  private resolveSelectedCompetitor(stats: any): any {
    const comps: any[] = Array.isArray(stats?.competitors) ? stats.competitors : [];
    if (!comps.length) return null;

    const sel = this.selectedTask?.selectedCompetitor;
    const selDomain = this.getDomain(sel?.domain || sel?.url || '');
    if (selDomain) {
      const match = comps.find(
        (c: any) => this.getDomain(c?.target || '') === selDomain,
      );
      if (match) return match;
    }
    return comps[0];
  }

  // Two periods (previous → current): your line + the selected competitor's.
  // Same Google LineChart as UX/CR, charting error counts instead of scores.
  private buildErrorTrend(ct: any, comps: any[]): void {
    // Remember the inputs so a legend toggle can re-plot without a refetch.
    this.lastErrorCt = ct;
    this.lastErrorComps = comps || [];

    const dates: string[] = [
      ct?.previousPeriod?.startDate,
      ct?.currentPeriod?.startDate,
    ].filter((d) => !!d);

    if (dates.length < 2) {
      this.errorTrendHasData = false;
      this.errorTrendChart.dataTable = [];
      this.errorTrendLegend = [];
      return;
    }

    // Every tracked competitor with a stable color; visible ones are plotted,
    // all appear in the legend.
    const enriched = (comps || [])
      .filter((c: any) => !!c)
      .map((c: any, idx: number) => ({
        raw: c,
        key: this.getDomain(c?.target || '') || `comp-${idx}`,
        label: this.getDomain(c?.target || '') || `Competitor ${idx + 1}`,
        color: COMP_PALETTE[idx % COMP_PALETTE.length],
      }));
    const visibleComps = enriched.filter((c) => this.isTrendVisible(c.key));

    const entities: {
      name: string;
      color: string;
      values: (number | null)[];
    }[] = [
      {
        name: 'Your site',
        color: ACCENT,
        values: [
          this.numOrNull(ct?.previousPeriod?.totalErrors),
          this.numOrNull(ct?.currentPeriod?.totalErrors),
        ],
      },
      ...visibleComps.map((c) => ({
        name: c.label,
        color: c.color,
        values: [
          this.numOrNull(c.raw?.previousPeriod?.totalErrors),
          this.numOrNull(c.raw?.currentPeriod?.totalErrors),
        ],
      })),
    ];

    // header: Period, then [name, tooltip] per entity
    const header: any[] = ['Period'];
    entities.forEach((e) => {
      header.push(e.name, { role: 'tooltip', p: { html: true } });
    });

    // Numeric/continuous x-axis (0..n-1) so the first point sits on the y-axis.
    const rows = dates.map((d, i) => {
      const full = this.fmtDateFull(d);
      const tip =
        `<p class='tooltip-text'><b>${full}</b></p>` +
        entities
          .map(
            (e) =>
              `<p class='tooltip-text'><b>${e.name}: </b>${
                e.values[i] !== null ? e.values[i] : '—'
              } errors</p>`,
          )
          .join('');

      const row: any[] = [i];
      entities.forEach((e) => {
        row.push(e.values[i], e.values[i] !== null ? tip : null);
      });
      return row;
    });

    const n = dates.length;
    this.errorTrendChart.dataTable = rows.length ? [header, ...rows] : [];
    this.errorTrendChart.options['colors'] = entities.map((e) => e.color);
    // Hide Google's built-in legend; render our own clickable dash legend.
    this.errorTrendChart.options.legend.position = 'none';
    this.errorTrendLegend = [
      { key: 'you', label: 'Your site', color: ACCENT, isYou: true, visible: true },
      ...enriched.map((c) => ({
        key: c.key,
        label: c.label,
        color: c.color,
        isYou: false,
        visible: this.isTrendVisible(c.key),
      })),
    ];
    this.errorTrendChart.options.hAxis.ticks = dates.map((d, i) => ({
      v: i,
      f: this.fmtShort(d),
    }));
    this.errorTrendChart.options.hAxis.viewWindow = { min: 0, max: n > 1 ? n - 1 : 1 };

    // vAxis auto-scales to the error counts (not fixed 0–100 like the score charts).
    const allVals = entities
      .flatMap((e) => e.values)
      .filter((v): v is number => typeof v === 'number');
    const maxVal = Math.max(4, ...allVals);
    this.errorTrendChart.options.vAxis.viewWindow = { min: 0, max: this.niceMax(maxVal) };

    this.applyErrorChartTheme();
    this.errorTrendHasData = rows.length > 0;
    setTimeout(() => this.errorTrendChart.component?.draw(this.errorTrendChart), 0);
  }

  private isTrendVisible(key: string): boolean {
    return this.trendVisible[key] !== false;
  }
  // Legend click — toggle the competitor line ("Your site" is always shown).
  toggleTrendLine(item: any) {
    if (!item || item.isYou) return;
    this.trendVisible[item.key] = !this.isTrendVisible(item.key);
    this.buildErrorTrend(this.lastErrorCt, this.lastErrorComps);
  }

  private numOrNull(v: any): number | null {
    return typeof v === 'number' && !isNaN(v) ? v : null;
  }

  // Round the axis max up to a clean value so gridlines read nicely.
  private niceMax(v: number): number {
    if (v <= 5) return 5;
    if (v <= 10) return 10;
    const mag = Math.pow(10, Math.floor(Math.log10(v)));
    return Math.ceil(v / mag) * mag;
  }

  private applyErrorChartTheme(): void {
    const o: any = this.errorTrendChart.options;
    o.backgroundColor = 'transparent';
    if (this.isDark) {
      o.legend.textStyle.color = '#cfccc6';
      o.vAxis.gridlineColor = '#3b3a3a';
      o.vAxis.baselineColor = '#3b3a3a';
      o.vAxis.textStyle.color = '#cfccc6';
      o.hAxis.textStyle.color = '#cfccc6';
      o.crosshair.color = '#3b3a3a';
    } else {
      o.legend.textStyle.color = '#666';
      o.vAxis.gridlineColor = '#ededed';
      o.vAxis.baselineColor = '#c0c0c0';
      o.vAxis.textStyle.color = '#666';
      o.hAxis.textStyle.color = '#666';
      o.crosshair.color = '#eeeeee';
    }
  }

  private fmtShort(date: string): string {
    try {
      return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return date;
    }
  }
  private fmtDateFull(date: string): string {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return date;
    }
  }

  private getDomain(url: string): string {
    if (!url) return '';
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;
      return new URL(normalized).hostname.replace(/^www\./, '');
    } catch {
      return url.replace(/^www\./, '');
    }
  }

  // ─── Style helpers ───────────────────────────────────────────────────────

  impactClass(impact: string): string {
    const i = (impact || '').toLowerCase();
    if (i === 'high') return 'critical';
    if (i === 'medium') return 'warning';
    return 'muted';
  }

  scoreClass(score: number): string {
    if (typeof score !== 'number') return 'muted';
    if (score >= 80) return 'critical';
    if (score >= 50) return 'warning';
    return 'good';
  }
}
